import { useCollections } from "api/collections";
import { getErrorMessage } from "api/errors";
import { hasViewAccessToResource } from "api/general";
import { InlineInput } from "components/FormControls/InlineInput/InlineInput";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ImportCsv } from "components/Layout/ImportCsv/ImportCsv";
import { ImportCsvModal } from "components/Layout/ImportCsv/ImportCsvModal/ImportCsvModal";
import { ImportStartedModal } from "components/Layout/ImportCsv/ImportStartedModal/ImportStartedModal";
import { OLTable, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Formik, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { SINGLE_COLLECTION_VALUES_LIST_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { links } from "utilities/links";
import AddValueModal from "./Modals/AddValueModal";

export enum Modals {
  ImportValues,
  UpdateValues,
  ImportStarted,
  DeleteValue,
  AddValue,
}

export const SingleCollection = () => {
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { listing, updateListing } = useListing(
    SINGLE_COLLECTION_VALUES_LIST_ID,
  );
  const { id: singleCollectionId } = useParams<{ id: string }>();
  const {
    useSingleCollection,
    useSingleCollectionValues,
    importCollectionValues,
    editCollectionValue,
    deleteCollectionValue,
  } = useCollections();

  const { data: singleCollectionData } = useSingleCollection(
    singleCollectionId as string,
  );

  const { data: singleCollectionValues } = useSingleCollectionValues(
    singleCollectionId as string,
    {
      order: listing.order || "asc",
      page: listing.page,
      rows: listing.rowsPerPage,
      sortKey: listing.property || "value",
    },
  );
  const [selectedModal, setSelectedModal] = useState<{
    mode: Modals | null;
    isOpen: boolean;
    valueId?: string;
  }>({ mode: null, isOpen: false });

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "collections" }),
      path: links.collectionsList,
    },
  };

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "value" }),
          isSortable: true,
          property: "value",
        },
        {
          headerText: message({ id: "description" }),
          isSortable: true,
          property: "description",
        },
      ]),
    [message],
  );

  const submitInlineEdit = useCallback(
    async <T extends { [key: string]: string | undefined }>(
      valueId: string,
      fieldName: keyof T & ("value" | "description"),
      newValue: string,
      originalItem: { value: string; description?: string },
      helpers: FormikHelpers<T>,
    ) => {
      const { setSubmitting, resetForm, setStatus } = helpers;
      setSubmitting(true);
      setStatus({});
      try {
        await editCollectionValue(valueId, {
          value: fieldName === "value" ? newValue : originalItem.value,
          description:
            fieldName === "description" ? newValue : originalItem.description,
        });
        notify(message({ id: "itemUpdated" }));
      } catch (error) {
        notify(getErrorMessage(error));
        resetForm({
          values: {
            [fieldName]: originalItem[fieldName],
          } as T,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [notify, message, editCollectionValue],
  );

  const submitDeleteCollectionValue = async () => {
    try {
      const response = await deleteCollectionValue(
        selectedModal.valueId as string,
      );

      if (response) {
        setSelectedModal({ mode: null, isOpen: false, valueId: "" });
        notify(message({ id: "itemSuccessfullyDeleted" }));
      }
    } catch (error) {
      notify(getErrorMessage(error));
    }
  };

  const itemsList = useMemo(
    () =>
      singleCollectionValues?.items.map((item) => ({
        cells: [
          {
            content: (
              <Formik
                key={`${item.valueId}-value`}
                initialValues={{ value: item.value }}
                onSubmit={(values, helpers) =>
                  submitInlineEdit(
                    item.valueId,
                    "value",
                    values.value,
                    item,
                    helpers,
                  )
                }
              >
                {({ handleSubmit }) => (
                  <InlineInput
                    name="value"
                    autoFocus
                    onEditModeClose={() => handleSubmit()}
                    iconDescription="edit"
                    options={{
                      maxLength: 64,
                    }}
                    data-test-id="value"
                  />
                )}
              </Formik>
            ),
            property: "value",
            disableOverflowTooltip: true,
          },
          {
            content: (
              <Formik
                key={`${item.valueId}-description`}
                initialValues={{ description: item.description }}
                onSubmit={(values, helpers) =>
                  submitInlineEdit(
                    item.valueId,
                    "description",
                    values.description || "",
                    item,
                    helpers,
                  )
                }
              >
                {({ handleSubmit }) => (
                  <InlineInput
                    name="description"
                    autoFocus
                    onEditModeClose={() => handleSubmit()}
                    iconDescription="edit"
                    placeholder="-"
                    options={{
                      maxLength: 64,
                    }}
                    data-test-id="description"
                  />
                )}
              </Formik>
            ),
            property: "description",
            disableOverflowTooltip: true,
          },
        ],
        id: item.valueId,
        testId: "single-group-values-list-row",
      })),
    [singleCollectionValues?.items, submitInlineEdit],
  );

  const items = singleCollectionValues?.items ? itemsList : [];
  const areSingleCollectionValues = !!singleCollectionValues?.items.length;
  const isLoading = !singleCollectionValues;
  const hasViewImportPermission = hasViewAccessToResource("IMPORT");

  return (
    <>
      <TopHeader
        title={
          singleCollectionData
            ? `${singleCollectionData?.name} ${message({
                id: "collection",
              }).toLowerCase()}`
            : ""
        }
        clearLink={links.collectionsList}
        breadcrumbs={breadcrumbs}
        badge={<StatusValue status={!!singleCollectionData?.active} />}
      />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderActionButton
            text={message({
              id: "addSingleItem",
            })}
            data-test-id="add-single-item"
            onClick={() => {
              setSelectedModal({
                mode: Modals.AddValue,
                isOpen: true,
              });
            }}
            variant="outlined"
          />
          <SubheaderActionButton
            text={message({
              id: areSingleCollectionValues ? "updateValues" : "importValues",
            })}
            data-test-id={
              areSingleCollectionValues ? "update-values" : "import-values"
            }
            onClick={() => {
              setSelectedModal({
                mode: areSingleCollectionValues
                  ? Modals.UpdateValues
                  : Modals.ImportValues,
                isOpen: true,
              });
            }}
            variant="contained"
            disabled={hasViewImportPermission}
          />
        </SubheaderWrapper>
      </Subheader>
      {isLoading ? (
        <OLTable
          fullView
          onSort={() => {}}
          tableName={SINGLE_COLLECTION_VALUES_LIST_ID}
        />
      ) : areSingleCollectionValues ? (
        <OLTable
          fullView
          setColumnOrdering={setColumnOrdering}
          tableName={SINGLE_COLLECTION_VALUES_LIST_ID}
          data-test-id="single-group-values"
          actions={{
            body: [
              {
                action: (
                  id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
                ) =>
                  setSelectedModal({
                    mode: Modals.DeleteValue,
                    isOpen: true,
                    valueId: id as string,
                  }),
                label: message({ id: "deleteItem" }),
              },
            ],
          }}
          columns={singleCollectionValues && headItems}
          onSort={(prop) =>
            updateListing({
              payload: prop,
              type: ListingActions.SET_SORT,
            })
          }
          pagination={{
            count: calculateCount(
              singleCollectionValues?.total.filtered,
              singleCollectionValues?.total.all,
            ),
            onChangePage: (page) => {
              updateListing({
                type: ListingActions.CHANGE_PAGE,
                payload: page,
              });
            },
            onChangeRowsPerPage: (rows) => {
              updateListing({
                type: ListingActions.CHANGE_ROWS_PER_PAGE,
                payload: rows,
              });
            },
            page: listing.page,
            rowsPerPage: listing.rowsPerPage,
          }}
          rows={singleCollectionValues && items}
          sort={{
            order: listing.order || "asc",
            property: listing.property || "value",
            rowsPerPage: listing.rowsPerPage,
            page: listing.page,
          }}
        />
      ) : (
        <ImportCsv
          title="collectionFirstValuesInfo"
          subtitle="collectionImportValuesInfo"
          actionButtonText="importValues"
          guideLink="https://help.openloyalty.io/group-of-values"
          openModal={() => {
            setSelectedModal({ mode: Modals.ImportValues, isOpen: true });
          }}
        />
      )}
      {selectedModal.mode === Modals.UpdateValues && (
        <ConfirmationModal
          action={() => {
            setSelectedModal({ mode: Modals.ImportValues, isOpen: true });
          }}
          close={() => setSelectedModal({ mode: null, isOpen: false })}
          header="importCsvConfirmationQuestion"
          info="importCsvConfirmationInfo"
          open={selectedModal.isOpen}
          cancelText={message({ id: "exit" })}
          submitText="importNewValues"
        />
      )}
      {selectedModal.mode === Modals.ImportValues && (
        <ImportCsvModal
          headerMessage="importCollection"
          closeModal={() => setSelectedModal({ mode: null, isOpen: false })}
          isModalOpen={selectedModal.isOpen}
          openImportStartedModal={() =>
            setSelectedModal({ mode: Modals.ImportStarted, isOpen: true })
          }
          importData={importCollectionValues}
        />
      )}
      {selectedModal.mode === Modals.ImportStarted && (
        <ImportStartedModal
          header="importValues"
          isOpen={selectedModal.isOpen}
          onClose={() => {
            setSelectedModal({ mode: null, isOpen: false });
            navigate(links.importsExports, { state: { activeTab: "imports" } });
          }}
          action={() => setSelectedModal({ mode: null, isOpen: false })}
        />
      )}
      {selectedModal.mode === Modals.DeleteValue && (
        <ConfirmationModal
          action={() => submitDeleteCollectionValue()}
          close={() =>
            setSelectedModal({ mode: null, isOpen: false, valueId: "" })
          }
          header="deleteItemQuestion"
          info="youCantUndoThisAction"
          open={selectedModal.isOpen}
          cancelText="cancel"
          submitText="yesDelete"
          modalWidth={545}
          data-test-id="delete-value-modal"
        />
      )}
      {selectedModal.mode === Modals.AddValue && (
        <AddValueModal
          isOpen={selectedModal.isOpen}
          setSelectedModal={setSelectedModal}
        />
      )}
    </>
  );
};
